import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Form } from 'antd';
import { NamePath } from 'antd/es/form/interface';
import { numberOrString } from 'gridstack';
import { useContext, useEffect } from 'react';
import { XmSelect } from 'src/component/select/XmSelect';
import { AdditionalApiPropsContext, RuleContext, invalidHwProps } from '../XmRule';
import { RuleGroup } from '../XmRuleList';
import { AllItemsContext } from '../comp/AllItems';
import { AllTagsContext } from '../comp/AllTags';
import TagSelect from '../comp/TagSelect';
import { makePreviewApiData } from '../comp/preview/CgPreviewHandler';
import { usePreviewModal } from '../comp/preview/Preview';
import { Option } from "../hook/useParentTypes";
import AddButton from './AddButton';
export type ArrayElement<T> = T extends (infer U)[] ? U : never;
function TagList({ parentTypes }: { parentTypes: Option[]; }) {
  const { cls, subsystem: subsys, hw_type } = useContext(AllItemsContext);
  const { fetchStarted, startFetch } = useContext(AllTagsContext);
  const { getValue, getPath, filterByParents, linuxExclude, mountPoint } = useContext(RuleContext);
  const invalidBaseProps = invalidHwProps({ cls, hw_type, subsystem: subsys });
  const [showPreview, contextHolder] = usePreviewModal();
  const { preview, additionalPayload } = useContext(AdditionalApiPropsContext);

  useEffect(() => {
    if (!fetchStarted)
      startFetch();
  }, []);

  const handlePreview = (subsystem: string, tag: string) => () => {
    const data = { ...makePreviewApiData({ cls, hwType: hw_type, subsystem, tags: [{ tag_id: tag, tag: '' }] }, linuxExclude), ...additionalPayload };
    showPreview({
      subsystem,
      hw_type,
      parent: true,
      additionalApiParams: preview?.parents?.item,
      data
    });
  };

  const pathStart = [...mountPoint, 'parents', 'tags'];
  const getDependencies = dependenciesMaker(pathStart, ['tag_id']);
  const getDependenciesSubsystem = dependenciesMaker(pathStart, ['subsystem']);

  const handleRemoveRegex = (remove: (index: number | number[]) => void, index: number) => () => {
    remove(index);
    filterByParents();
  };

  return <>
    <Form.List name={getPath(['parents', 'tags'])} >
      {(tagsItems, { remove, add }) => {
        return <>
          <div className='xm-rule-filter-tag'>
            {tagsItems.map(({ key, name }) => {
              const tag = getValue<ArrayElement<RuleGroup['parents']['tags']>>(['parents', 'tags', name]);
              return <span className='xm-tag-item' key={`parent-tag-list-${key}`}>
                <Form.Item
                  name={[name, 'subsystem']}
                  noStyle
                  rules={[
                    { required: true },
                  ]}
                >
                  <XmSelect
                    className='xm-tag-subsystem'
                    placeholder='Subsystem'
                    size='small'
                    options={parentTypes}
                    onChange={filterByParents}
                    ignoreReadonly
                  />
                </Form.Item>
                <span className='xm-tag-item-container'>
                  <Form.Item
                    name={[name, 'tag_id']}
                    help={<></>}
                    dependencies={[...getDependencies(tagsItems.length, name), ...getDependenciesSubsystem(tagsItems.length)]}
                    rules={[
                      { required: true },
                      sameTagRuleMaker(getValue<RuleGroup['parents']['tags']>(['parents', 'tags']))(name)
                    ]}
                  >
                    <TagSelect selectProps={{ className: 'xm-tag-select', disabled: !tag.subsystem }} />
                  </Form.Item>
                  <Button
                    disabled={invalidBaseProps || !tag.tag_id}
                    size='small'
                    icon={<EyeOutlined />}
                    onClick={handlePreview(tag.subsystem, tag.tag_id)}
                  />
                </span>
                <Button
                  size='small'
                  className={invalidBaseProps || getValue<RuleGroup['parents']['tags']>(['parents', 'tags'])?.length < 2 ? 'xm-disabled-delete' : 'hover-danger'}
                  disabled={invalidBaseProps || getValue<RuleGroup['parents']['tags']>(['parents', 'tags'])?.length < 2}
                  type='link'
                  title='Remove tag'
                  icon={<DeleteOutlined onClick={handleRemoveRegex(remove, name)} />} />
              </span>;
            })}
          </div>
          <AddButton disabled={!getValue<RuleGroup['parents']['tags']>(['parents', 'tags'])} onClick={() => add({
            tag_id: undefined,
            tag: undefined,
            subsystem: undefined
          })} />
        </>;
      }}
    </Form.List >
    {contextHolder}
  </>;
}

export const dependenciesMaker = (pathStart: numberOrString[], pathEnd: numberOrString[] = []) => (length: number, excludeSelf?: number) => {
  const deps: NamePath<(string | number)[]>[] = [];

  for (let i = 0; i < length; i++) {
    if (!excludeSelf || excludeSelf != i)
      deps.push([...pathStart, i, ...pathEnd]);
  }

  return deps;
};

const sameTagRuleMaker = (tags: {
  tag_id: string;
  tag: string;
  subsystem: string;
}[]) => (tagIndx: number) => {
  const validator = (_, val: string) => {
    if (tags.some((tag, indx) => tag.tag_id == val && indx !== tagIndx && tags[tagIndx].subsystem == tag.subsystem))
      return Promise.reject(new Error('Tag is already in use with the same subsystem'));

    return Promise.resolve();
  };
  return { validator };
};

export default TagList;